export enum MapStoreyProperty {
  StoreyHeight = 'storey_height',
  Elevation = 'elevation',
  TopHeight = 'top_height',
  Level = 'level',
  Mezzanine = 'mezzanine',
  Extrude = 'extrude',
  Selected = 'selected',
  Hover = 'hover',
  Transparent = 'transparent',
}

export enum PointIconId {
  Default = 'circle-icon',
  Selected = 'circle-icon-selected',
}

export type MapStoreyProperties = {
  [MapStoreyProperty.StoreyHeight]: number;
  [MapStoreyProperty.Elevation]: number;
  [MapStoreyProperty.TopHeight]: number;
  [MapStoreyProperty.Level]: number;
  [MapStoreyProperty.Mezzanine]: boolean;
  [MapStoreyProperty.Selected]: boolean;
  [MapStoreyProperty.Hover]: boolean;
  [MapStoreyProperty.Transparent]: boolean;
};

export type MapStoreyPolygonProperties = MapStoreyProperties & {
  id: string;
  /** Owning building id when the polygon is a static other-building footprint. */
  buildingId?: string;
  /**
   * Invisible map click target for a sibling with no footprint yet.
   * Must not be painted as a building.
   */
  emptyHitTarget?: boolean;
};

/** GeoJSON property used to hide empty-building hit targets. */
export const EMPTY_BUILDING_HIT_TARGET_PROPERTY =
  'emptyHitTarget' satisfies keyof MapStoreyPolygonProperties;

export type MapStoreyPointProperties = MapStoreyProperties & {
  index: number;

  /**
   * Draft shape does not have an id
   */
  shapeId?: string;

  /**
   * Whether the point is a draft point
   */
  draft?: boolean;

  /**
   * Whether this is the first point in a draft shape (used to detect closing clicks)
   */
  isFirst?: boolean;

  /**
   * Marks virtual midpoint point rendered while hovering a line.
   */
  insertCandidate?: boolean;

  /**
   * Line segment metadata for the virtual insert candidate.
   */
  insertStartIndex?: number;
  insertEndIndex?: number;
};

/**
 * A GeoJSON feature representing a storey polygon
 */
export type MapStoreyPolygonFeature = GeoJSON.Feature<
  GeoJSON.Polygon,
  MapStoreyPolygonProperties
>;

/**
 * A GeoJSON feature representing a storey points
 */
export type MapStoreyPointFeature = GeoJSON.Feature<
  GeoJSON.Point,
  MapStoreyPointProperties
>;

/**
 * A GeoJSON feature representing a storey point extrusion (for 3D rendering)
 */
export type MapStoreyPointExtrusionFeature = GeoJSON.Feature<
  GeoJSON.Polygon,
  MapStoreyPointProperties
>;

export type MapStoreyFeature = MapStoreyPolygonFeature | MapStoreyPointFeature;

/**
 * A GeoJSON object representing a collection of storey features
 */
export type MapStoreyGeoJson<T extends MapStoreyFeature = MapStoreyFeature> = {
  type: 'geojson';
  data: {
    type: 'FeatureCollection';
    features: T[];
  };
};
